import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { UserProvider } from "@/contexts/UserContext";
import { Suspense } from "react";
import LoadingSpinner from "@/components/LoadingSpinner";
import { AuthProvider } from "@/contexts/AuthContext";
import { NotificationProvider } from "@/components/NotificationSystem";
import ErrorBoundary from "@/components/ErrorBoundary";

// Import pages directly
import Index from "./pages/Index";
import Login from './pages/Login';
import Marketplace from "./pages/MarketplaceFixed";
import ServiceDetail from "./pages/ServiceDetail";
import ProductDetail from "./pages/ProductDetail";
import AppointmentCalendar from "./pages/AppointmentCalendar";
import JobDetail from "./pages/JobDetail";
import Profile from "./pages/ProfileFixed";
import AddProduct from "./pages/AddProduct";
import AddService from "./pages/AddService";
import Messages from "./pages/Messages";
import NotFound from "./pages/NotFound";
import ServicesList from "./pages/ServicesList";
import Test from "./pages/Test";
import EditService from "./pages/EditService";
import EditProduct from "./pages/EditProduct";
import MyAppointments from "./pages/MyAppointments";
import Dashboard from "./pages/Dashboard";

// Simple Query Client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes
      refetchOnWindowFocus: false,
      retry: 1, // Reduce retries to avoid hanging
    },
  },
});

// Simple loading fallback
const LoadingFallback = () => (
  <div style={{
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px'
  }}>
    <div style={{
      background: 'white',
      padding: '30px',
      borderRadius: '15px',
      textAlign: 'center',
      boxShadow: '0 10px 30px rgba(0,0,0,0.1)'
    }}>
      <div style={{
        width: '60px',
        height: '60px',
        background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        margin: '0 auto 20px auto',
        animation: 'pulse 2s ease-in-out infinite'
      }}>
        <span style={{
          color: 'white',
          fontSize: '20px',
          fontWeight: 'bold'
        }}>WOM</span>
      </div>
      <p style={{
        color: '#374151',
        fontSize: '16px',
        margin: 0
      }}>Loading WOM Plaza...</p>
    </div>
  </div>
);

function App() {
  console.log('WOM Plaza App starting...');

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <NotificationProvider>
          <AuthProvider>
            <UserProvider>
              <TooltipProvider>
                <Toaster />
                <BrowserRouter>
                  <Suspense fallback={<LoadingFallback />}>
                    <Routes>
                      <Route path="/" element={<Index />} />
                      <Route path="/test" element={<Test />} />
                      <Route path="/login" element={<Login />} />
                      <Route path="/marketplace" element={<Marketplace />} />
                      <Route path="/profiel" element={<Profile />} />
                      <Route path="/dienst-plaatsen" element={<AddService />} />
                      <Route path="/services/toevoegen" element={<AddService />} />
                      <Route path="/berichten" element={<Messages />} />
                      <Route path="/diensten" element={<ServicesList />} />
                      <Route path="/service/:id" element={<ServiceDetail />} />
                      <Route path="/product/:id" element={<ProductDetail />} />
                      <Route path="/job/:id" element={<JobDetail />} />
                      <Route path="/agenda" element={<AppointmentCalendar />} />
                      <Route path="/afspraken" element={<MyAppointments />} />
                      <Route path="/product/toevoegen" element={<AddProduct />} />
                      <Route path="/add-service/edit/:id" element={<EditService />} />
                      <Route path="/add-product/edit/:id" element={<EditProduct />} />
                      <Route path="/dashboard" element={<Dashboard />} />
                      <Route path="*" element={<NotFound />} />
                    </Routes>
                  </Suspense>
                </BrowserRouter>
              </TooltipProvider>
            </UserProvider>
          </AuthProvider>
        </NotificationProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;